import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Alert,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Avatar,
  Checkbox,
  LinearProgress,
  Button,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { Close, Send, AttachFile } from "@mui/icons-material";
import { priorityConfig } from "./TaskCard";

const mapStaffToUser = (staff) => {
  if (!staff) return { id: "unassigned", name: "Unassigned", avatar: "U", email: "", department: "" };
  return {
    id: staff.Id || staff.id,
    name: staff.name || `${staff.FirstName || ""} ${staff.LastName || ""}`.trim(),
    email: staff.email || "",
    avatar: staff.avatar || `${(staff.FirstName || "?")[0]}${(staff.LastName || "?")[0]}`.toUpperCase(),
    department: staff.department || "General",
    image: staff.image || null,
  };
};

export default function TaskDetailPanelMui({
  task,
  isCreating,
  onClose,
  onSave,
  onDelete,
  readOnly = false,
  staffsList = [], // Real staff list from the parent
}) {
  const [formData, setFormData] = useState(() => ({
    id: "",
    title: "",
    description: "",
    priority: "medium",
    status: "todo",
    dueDate: new Date().toISOString().split("T")[0],
    assignee: { name: "Unassigned", avatar: "U", id: "unassigned" },
    comments: [],
    attachments: [],
    likes: 0,
    completedSubtasks: 0,
    totalSubtasks: 0,
    recurring: false,
    subtasks: [],
    collaborators: [],
  }));

  const [newComment, setNewComment] = useState("");
  const [newSubtaskText, setNewSubtaskText] = useState("");
  const [saveError, setSaveError] = useState(null);

  // Sync form data when task prop changes
  useEffect(() => {
    if (task && !isCreating) {
      setFormData({
        id: task.id || "",
        title: task.title || "",
        description: task.description || "",
        priority: task.priority || "medium",
        status: task.status || "todo",
        dueDate: task.dueDate ? task.dueDate.split("T")[0] : new Date().toISOString().split("T")[0],
        assignee: task.assignee || { name: "Unassigned", avatar: "U", id: "unassigned" },
        comments: Array.isArray(task.comments) ? task.comments : [],
        attachments: Array.isArray(task.attachments) ? task.attachments : [],
        likes: task.likes || 0,
        completedSubtasks: task.completedSubtasks || 0,
        totalSubtasks: task.totalSubtasks || 0,
        recurring: task.recurring || false,
        subtasks: Array.isArray(task.subtasks) ? task.subtasks : [],
        collaborators: Array.isArray(task.collaborators) ? task.collaborators : [],
      });
    } else if (isCreating) {
      setFormData({
        id: "",
        title: "",
        description: "",
        priority: "medium",
        status: "todo",
        dueDate: new Date().toISOString().split("T")[0],
        assignee: { name: "Unassigned", avatar: "U", id: "unassigned" },
        comments: [],
        attachments: [],
        likes: 0,
        completedSubtasks: 0,
        totalSubtasks: 0,
        recurring: false,
        subtasks: [],
        collaborators: [],
      });
    }
  }, [task, isCreating]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaveError(null);
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      setFormData((prev) => ({
        ...prev,
        comments: [
          ...prev.comments,
          {
            id: Date.now().toString(),
            text: newComment.trim(),
            author: "You",
            time: new Date().toLocaleString(),
          },
        ],
      }));
      setNewComment("");
    }
  };

  const handleToggleSubtask = (subtaskId, completed) => {
    setFormData((prev) => {
      const updatedSubtasks = prev.subtasks.map((st) =>
        st.id === subtaskId ? { ...st, completed } : st
      );
      const completedCount = updatedSubtasks.filter((st) => st.completed).length;
      return {
        ...prev,
        subtasks: updatedSubtasks,
        completedSubtasks: completedCount,
        totalSubtasks: updatedSubtasks.length,
      };
    });
  };

  const handleAddSubtask = () => {
    if (newSubtaskText.trim()) {
      setFormData((prev) => {
        const newSub = {
          id: Date.now().toString(),
          title: newSubtaskText.trim(),
          completed: false,
        };
        const updatedSubtasks = [...prev.subtasks, newSub];
        return {
          ...prev,
          subtasks: updatedSubtasks,
          totalSubtasks: updatedSubtasks.length,
          completedSubtasks: updatedSubtasks.filter((st) => st.completed).length,
        };
      });
      setNewSubtaskText("");
    }
  };

  const handleDeleteSubtask = (subtaskId) => {
    setFormData((prev) => {
      const updatedSubtasks = prev.subtasks.filter((st) => st.id !== subtaskId);
      return {
        ...prev,
        subtasks: updatedSubtasks,
        totalSubtasks: updatedSubtasks.length,
        completedSubtasks: updatedSubtasks.filter((st) => st.completed).length,
      };
    });
  };

  const handleCollaboratorsChange = (event) => {
    const selectedIds = event.target.value;
    const selectedStaffs = selectedIds
      .map((id) => staffsList.find((s) => s.Id === id || s.id === id))
      .filter(Boolean);
    const mappedCollabs = selectedStaffs.map(mapStaffToUser);
    handleChange("collaborators", mappedCollabs);
  };

  const handleSave = () => {
    try {
      if (!formData.title?.trim()) {
        setSaveError("Task title is required");
        return;
      }
      const dueDate = new Date(formData.dueDate);
      if (isNaN(dueDate.getTime())) {
        setSaveError("Invalid due date");
        return;
      }
      if (!["high", "medium", "low"].includes(formData.priority)) {
        setSaveError("Invalid priority level");
        return;
      }
      if (!["todo", "in_progress", "review", "completed"].includes(formData.status)) {
        setSaveError("Invalid status");
        return;
      }
      onSave(formData);
    } catch (error) {
      console.error("Error saving task:", error);
      setSaveError("An error occurred while saving. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        right: 0,
        top: 0,
        height: "100vh",
        width: { xs: "100%", sm: "600px" },
        bgcolor: "#FCFDFE",
        boxShadow: "-10px 0 35px rgba(0,0,0,0.12)",
        zIndex: 1300,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "white",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: priorityConfig[formData.priority]?.color || "#717182",
            }}
          />
          <Typography variant="h6" fontWeight={600} color="#1A1A24">
            {isCreating ? "Create New Task" : "Task Workspace"}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: "#717182" }}>
          <Close />
        </IconButton>
      </Box>

      {/* Error Message */}
      {saveError && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="error" onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, p: 3.5, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Task Title */}
        <TextField
          fullWidth
          placeholder="Task Title"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
          variant="outlined"
          error={!formData.title?.trim() && !!saveError}
          disabled={readOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: 22,
              fontWeight: 700,
              color: "#1A1A24",
              p: 0,
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "none" },
            },
            "& .MuiInputBase-input": {
              p: 0,
            },
          }}
        />

        {/* Task Properties Box */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: "#F8FAFC",
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.05)",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2.5,
          }}
        >
          {/* Status Select */}
          <Box>
            <Typography fontSize={11} fontWeight={600} color="#717182" sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                disabled={readOnly}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#9e9e9e" }}>Set status</span>;
                  }
                  const statusLabels = {
                    todo: "To Do",
                    in_progress: "In Progress",
                    review: "Review",
                    completed: "Completed",
                  };
                  return statusLabels[selected] || selected;
                }}
                sx={{
                  fontSize: 13,
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.08)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6C5CE7" },
                }}
              >
                <MenuItem value="" disabled>
                  <span style={{ color: "#9e9e9e" }}>Set status</span>
                </MenuItem>
                <MenuItem value="todo">To Do</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="review">Review</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Priority Select */}
          <Box>
            <Typography fontSize={11} fontWeight={600} color="#717182" sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Priority
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={formData.priority}
                onChange={(e) => handleChange("priority", e.target.value)}
                disabled={readOnly}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#9e9e9e" }}>Set priority</span>;
                  }
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: priorityConfig[selected]?.color || "#ABABAB",
                        }}
                      />
                      {priorityConfig[selected]?.label || selected}
                    </Box>
                  );
                }}
                sx={{
                  fontSize: 13,
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.08)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6C5CE7" },
                }}
              >
                <MenuItem value="" disabled>
                  <span style={{ color: "#9e9e9e" }}>Set priority</span>
                </MenuItem>
                <MenuItem value="low">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: priorityConfig.low.color }} />
                    Low
                  </Box>
                </MenuItem>
                <MenuItem value="medium">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: priorityConfig.medium.color }} />
                    Medium
                  </Box>
                </MenuItem>
                <MenuItem value="high">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: priorityConfig.high.color }} />
                    High
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Assignee Select */}
          <Box>
            <Typography fontSize={11} fontWeight={600} color="#717182" sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Assignee
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={formData.assignee?.id || "unassigned"}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "unassigned") {
                    handleChange("assignee", { name: "Unassigned", avatar: "U", id: "unassigned" });
                  } else {
                    const matched = staffsList.find((s) => s.Id === val || s.id === val);
                    if (matched) handleChange("assignee", mapStaffToUser(matched));
                  }
                }}
                disabled={readOnly}
                sx={{
                  fontSize: 13,
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.08)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6C5CE7" },
                }}
              >
                <MenuItem value="unassigned">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar sx={{ width: 18, height: 18, fontSize: 10, bgcolor: "#ABABAB", color: "white" }}>U</Avatar>
                    Unassigned
                  </Box>
                </MenuItem>
                {staffsList.map((staff) => {
                  const u = mapStaffToUser(staff);
                  return (
                    <MenuItem key={u.id} value={u.id}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          src={u.image ? `https://mindsinaction.com.na/assets/media/pictures/${u.image}` : undefined}
                          sx={{ width: 18, height: 18, fontSize: 10, bgcolor: "#6C5CE7", color: "white" }}
                        >
                          {u.avatar}
                        </Avatar>
                        {u.name}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Due Date */}
          <Box>
            <Typography fontSize={11} fontWeight={600} color="#717182" sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Due Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              value={formData.dueDate ? formData.dueDate.split("T")[0] : ""}
              onChange={(e) => handleChange("dueDate", e.target.value)}
              disabled={readOnly}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 13,
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                  "&:hover fieldset": { borderColor: "#6C5CE7" },
                },
              }}
            />
          </Box>
        </Box>

        {/* Collaborators Multi-Select */}
        <Box>
          <Typography fontSize={11} fontWeight={600} color="#717182" sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Collaborators (Invite Team Members)
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={formData.collaborators.map((c) => c.id)}
              onChange={handleCollaboratorsChange}
              disabled={readOnly}
              input={<OutlinedInput size="small" />}
              renderValue={(selectedIds) => {
                if (selectedIds.length === 0) {
                  return <span style={{ color: "#9e9e9e", fontSize: 13 }}>Invite collaborators...</span>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {formData.collaborators.map((collab) => (
                      <Chip
                        key={collab.id}
                        avatar={
                          <Avatar
                            src={collab.image ? `https://mindsinaction.com.na/assets/media/pictures/${collab.image}` : undefined}
                            sx={{ width: 14, height: 14, fontSize: 8 }}
                          >
                            {collab.avatar}
                          </Avatar>
                        }
                        label={collab.name}
                        size="small"
                        sx={{ height: 22, fontSize: 11 }}
                      />
                    ))}
                  </Box>
                );
              }}
              sx={{
                bgcolor: "white",
                borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.08)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6C5CE7" },
              }}
            >
              {staffsList
                .filter((staff) => staff.Id !== formData.assignee?.id && staff.id !== formData.assignee?.id) // Cannot collaborate if already assignee
                .map((staff) => {
                  const u = mapStaffToUser(staff);
                  return (
                    <MenuItem key={u.id} value={u.id} sx={{ fontSize: 13 }}>
                      <Checkbox checked={formData.collaborators.some((c) => c.id === u.id)} size="small" sx={{ p: 0.5 }} />
                      <Avatar
                        src={u.image ? `https://mindsinaction.com.na/assets/media/pictures/${u.image}` : undefined}
                        sx={{ width: 18, height: 18, fontSize: 9, mr: 1, ml: 0.5 }}
                      >
                        {u.avatar}
                      </Avatar>
                      {u.name}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Box>

        {/* Task Description */}
        <Box>
          <Typography fontSize={12} fontWeight={600} color="#717182" sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Description
          </Typography>
          <TextField
            fullWidth
            placeholder="Write a description here..."
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            disabled={readOnly}
            multiline
            rows={4}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 14,
                bgcolor: "white",
                borderRadius: "10px",
                p: 2,
                "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                "&:hover fieldset": { borderColor: "#6C5CE7" },
              },
              "& .MuiOutlinedInput-input": {
                color: "black",
              },
            }}
          />
        </Box>

        {/* Subtasks Checklist */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography fontSize={12} fontWeight={600} color="#717182" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Subtasks ({formData.completedSubtasks || 0}/{formData.totalSubtasks || 0})
            </Typography>
            {formData.totalSubtasks > 0 && (
              <Typography fontSize={12} color="#6C5CE7" fontWeight={600}>
                {Math.round(((formData.completedSubtasks || 0) / (formData.totalSubtasks || 1)) * 100)}%
              </Typography>
            )}
          </Stack>

          {formData.totalSubtasks > 0 && (
            <LinearProgress
              variant="determinate"
              value={formData.totalSubtasks > 0 ? ((formData.completedSubtasks || 0) / formData.totalSubtasks) * 100 : 0}
              sx={{
                height: 5,
                borderRadius: 2.5,
                mb: 2,
                bgcolor: "#ECECF0",
                "& .MuiLinearProgress-bar": { bgcolor: "#6C5CE7", borderRadius: 2.5 },
              }}
            />
          )}

          <Stack gap={1} mb={2}>
            {formData.subtasks?.map((st) => (
              <Box
                key={st.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.25,
                  bgcolor: "white",
                  borderRadius: "8px",
                  border: "1px solid rgba(0,0,0,0.04)",
                  "&:hover": { bgcolor: "#F8FAFC" },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} flex={1}>
                  <Checkbox
                    checked={st.completed}
                    onChange={(e) => handleToggleSubtask(st.id, e.target.checked)}
                    disabled={readOnly}
                    size="small"
                    sx={{ p: 0, color: "#ABABAB", "&.Mui-checked": { color: "#6C5CE7" } }}
                  />
                  <Typography
                    fontSize={13.5}
                    sx={{
                      textDecoration: st.completed ? "line-through" : "none",
                      color: st.completed ? "#717182" : "#1A1A24",
                      userSelect: "none",
                    }}
                  >
                    {st.title}
                  </Typography>
                </Stack>
                {!readOnly && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSubtask(st.id)}
                    sx={{ color: "#717182", "&:hover": { color: "#EF4444" } }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>

          {!readOnly && (
            <Stack direction="row" gap={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a new subtask..."
                value={newSubtaskText}
                onChange={(e) => setNewSubtaskText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddSubtask();
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    bgcolor: "white",
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                    "&:hover fieldset": { borderColor: "#6C5CE7" },
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "black",
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddSubtask}
                sx={{
                  textTransform: "none",
                  borderColor: "#6C5CE7",
                  color: "#6C5CE7",
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "rgba(108,92,231,0.05)", borderColor: "#6C5CE7" },
                }}
              >
                Add
              </Button>
            </Stack>
          )}
        </Box>

        {/* Divider */}
        <Box sx={{ height: "1px", bgcolor: "rgba(0,0,0,0.05)", my: 1 }} />

        {/* Comments Feed */}
        {!isCreating && (
          <Box>
            <Typography fontSize={12} fontWeight={600} color="#717182" sx={{ mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Comments ({formData.comments?.length || 0})
            </Typography>

            {!readOnly && (
              <Stack direction="row" gap={1.5} mb={2.5}>
                <TextField
                  fullWidth
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.ctrlKey) {
                      handleAddComment();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 13,
                      bgcolor: "white",
                      borderRadius: "10px",
                      "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                      "&:hover fieldset": { borderColor: "#6C5CE7" },
                    },
                  }}
                />
                <IconButton
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  sx={{
                    alignSelf: "flex-end",
                    bgcolor: newComment.trim() ? "#6C5CE7" : "transparent",
                    color: newComment.trim() ? "white" : "#ABABAB",
                    "&:hover": { bgcolor: newComment.trim() ? "#5a4aa3" : "transparent" },
                    width: 36,
                    height: 36,
                  }}
                >
                  <Send sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            )}

            <Stack gap={1.5}>
              {formData.comments?.map((comment) => (
                <Box
                  key={comment.id}
                  sx={{
                    p: 2,
                    bgcolor: "white",
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.04)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 18, height: 18, fontSize: 9, bgcolor: "#6C5CE7" }}>
                        {comment.author ? comment.author[0] : "?"}
                      </Avatar>
                      <Typography fontSize={12} fontWeight={600} color="#1A1A24">
                        {comment.author}
                      </Typography>
                    </Stack>
                    <Typography fontSize={11} color="#717182">
                      {comment.time}
                    </Typography>
                  </Stack>
                  <Typography fontSize={13} color="#4A4A5A" sx={{ pl: 3 }}>
                    {comment.text}
                  </Typography>
                </Box>
              ))}
              {(!formData.comments || formData.comments.length === 0) && (
                <Typography fontSize={12} color="#717182" textAlign="center" py={2}>
                  No comments posted yet
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Attachments */}
        {!isCreating && formData.attachments?.length > 0 && (
          <Box>
            <Typography fontSize={12} fontWeight={600} color="#717182" sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Attachments ({formData.attachments.length})
            </Typography>
            <Stack gap={1}>
              {formData.attachments.map((att, idx) => (
                <Stack
                  key={idx}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  p={1.5}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    border: "1px solid rgba(0,0,0,0.04)",
                  }}
                >
                  <AttachFile sx={{ fontSize: 16, color: "#717182" }} />
                  <Typography fontSize={12.5} color="#1A1A24">{att.name || att}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          p: 2.5,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          gap: 1.5,
          bgcolor: "white",
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: "#1a1a1a",
            borderColor: "rgba(0,0,0,0.15)",
            borderRadius: "8px",
            fontSize: 13,
            "&:hover": { borderColor: "rgba(0,0,0,0.3)" },
          }}
        >
          Cancel
        </Button>
        {!readOnly && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: "none",
              bgcolor: "#6C5CE7",
              borderRadius: "8px",
              fontSize: 13,
              "&:hover": { bgcolor: "#5a4aa3" },
            }}
          >
            {isCreating ? "Create Task" : "Save Changes"}
          </Button>
        )}
      </Box>

      {!isCreating && !readOnly && (
        <Box sx={{ p: 2.5, borderTop: "1px solid rgba(0,0,0,0.06)", bgcolor: "white" }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={onDelete}
            sx={{ textTransform: "none", borderRadius: "8px", fontSize: 13 }}
          >
            Delete Task
          </Button>
        </Box>
      )}
    </Box>
  );
}
